/**
 * Total P&L de l'aperçu d'import.
 *
 * L'aperçu et l'import doivent appliquer le MÊME calcul, sinon l'utilisateur
 * voit un chiffre avant d'importer et un autre dans son journal après.
 *
 * Le piège : un trade Tradovate arrive sans commission (l'export ne la détaille
 * pas). Elle est dérivée au moment de l'import, à partir du taux de la modale
 * ou du template de la prop firm. Un aperçu qui lit `trade.commission ?? 0`
 * ignore donc tous les frais et annonce un résultat trop favorable.
 *
 * Convention du projet : la commission est stockée NÉGATIVE, et
 * netPnl = pnl + commission (cf. tradePhase.ts).
 */

export type PreviewTrade = {
  pnl: number;
  qty: number;
  /** Fournie par certaines plateformes (TopstepX). Absente chez Tradovate. */
  commission?: number;
  symbol: string;
};

/**
 * @param trades  Trades tels que sortis du parser.
 * @param ratePerContract Taux de commission par contrat saisi dans la modale.
 *   Le signe est ignoré : un taux est un coût, jamais un gain.
 * @param rateForSymbol Taux propre au symbole, tiré du template de la prop firm.
 *   L'import le préfère au taux de la modale : l'aperçu doit faire de même,
 *   sinon les deux chiffres divergent à nouveau.
 */
export function previewNetPnl(
  trades: PreviewTrade[],
  ratePerContract: number,
  rateForSymbol?: (symbol: string) => number | null,
): number {
  const fallback = Number.isFinite(ratePerContract) ? Math.abs(ratePerContract) : 0;

  return trades.reduce((sum, trade) => {
    // Une commission déjà portée par le trade fait foi : la recalculer
    // écraserait les frais réels de la plateforme par une estimation.
    if (trade.commission !== undefined) {
      return sum + trade.pnl + trade.commission;
    }

    const symbolRate = rateForSymbol?.(trade.symbol) ?? null;
    const raw = symbolRate ?? fallback;
    const rate = Number.isFinite(raw) ? Math.abs(raw) : 0;

    return sum + trade.pnl - rate * (trade.qty || 0);
  }, 0);
}
